"use client";

import React, { useState, useMemo, useEffect } from "react";
import PressTabs from "./PressTabs";
import PressList from "./PressList";
import {
  associatePartnershipCards,
  strategicPartnershipCards,
  referralPartnershipCards,
  type PartnershipType,
} from "./data";

export default function Press_release() {
  // PERF/CLS: the tab is read from the URL on mount instead of via
  // useSearchParams(). useSearchParams() forces a <Suspense> boundary, which
  // made this whole section SSR as nothing (fallback={null}) and pop in after
  // hydration — the page's main CLS source. With a mount-time read, the
  // default "associate" tab is part of the prerendered HTML, and visitors
  // arriving with ?tab=... switch right after hydration. PressTabs drives tab
  // changes through onChange directly, so no reactive params are needed.
  const [active, setActive] = useState<PartnershipType>("associate");

  useEffect(() => {
    const tabParam = new URLSearchParams(window.location.search).get(
      "tab",
    ) as PartnershipType | null;
    if (tabParam && ["associate", "strategic", "referral"].includes(tabParam)) {
      setActive(tabParam);
    }
  }, []);

  const items = useMemo(() => {
    switch (active) {
      case "associate":
        return associatePartnershipCards;
      case "strategic":
        return strategicPartnershipCards;
      case "referral":
        return referralPartnershipCards;
      default:
        return associatePartnershipCards;
    }
  }, [active]);

  const description = useMemo(() => {
    switch (active) {
      case "associate":
        return (
          <span>
            In our associate partner program, we act as an extension to your
            existing development setup. You can either choose to outsource the
            entire project to us or work collectively with us on a project, and
            we will assist and support you with our state-of-the-art
            infrastructure and highly skilled design and development teams.
            <br />
            <br /> As our associate partner, all your projects will be taken on
            priority. We would work for you on an hourly basis at a competitive
            price, which is exclusively applicable only for our Associate
            partners.
          </span>
        );
      case "strategic":
        return (
          <span>
            With our strategic partner program, you no longer need to worry
            about technical staff and its management, health, safety, pension,
            benefits and other responsibilities. You don't have to hire
            technical manpower and train or update them on different
            technologies. You don't have to think about changing technology, new
            equipment, software licenses and upgrades. All you need to do as our
            Strategic partner is to focus on getting more and more business.
            <br />
            <br />
            In this program, we work in a joint venture, where you act as the
            front-end sales executive and marketing group while we act as the
            back-end technical development group. Profits are shared on a
            pre-agreed percentage basis.
          </span>
        );
      case "referral":
        return (
          <span>
            Our referral partnership program is outstandingly rewarding and
            comes without any concerns of complex contracts or commitments. By
            becoming our referral partner, you can market our products, services
            and solutions to potential clients across the world. And for each of
            the closed deals from these referred projects, you receive
            commissions in the form of credit on all future projects that you
            bring to us. We would be responsible for finalizing the deal and
            working out the details from your referrals without any effort from
            your side. Rest assured, the clients you refer to us will be
            grateful to you.
          </span>
        );
      default:
        return "";
    }
  }, [active]);

  return (
    <section className="py-[50px] pb-[120px] bg-[#fff]">
      <div className="max-w-[1720px] mx-auto w-[calc(100%_-_10%)]">
        <PressTabs active={active} onChange={setActive} />
      </div>

      <div className="max-w-[5000px] mx-auto w-[calc(100%_-_10%)] pt-[54px]">
        <p className="text-[#00040b] text-[clamp(0.9375rem,0.4664rem+0.9815vw,2rem)] leading-[150%] whitespace-pre-line">
          {description}
        </p>
      </div>

      <div className="max-w-[5000px] mx-auto w-[calc(100%_-_10%)] pt-[54px]">
        <PressList items={items} />
      </div>
    </section>
  );
}
